import { Fetcher } from "@/utils/fetcher";
import { AxiosRequestConfig } from "axios";

const common = new Fetcher();
const product = new Fetcher("product");
const review = new Fetcher("review");
const notify = new Fetcher("notify");
const fileUpload = new Fetcher("file-uploader");
const question = new Fetcher("ask-question");


// get product by slug
export const getProductBySlug = (slug: string) =>
  product.get<ProductDetails>(`/storefront/${slug}`);

// get product by ids
export const getProductByIds = (ids: number[]) =>
  product.post<ProductListResponse>("/storefront/get-by-ids", { ids });

// get product by ids V2
export const getProductByIdsV2 = (ids: number[]) =>
  product.post<ProductListResponse<ProductItemV2>>("/storefront/get-by-ids", {
    ids,
  });

// get products by logic: {{apiUrl}}/api/v1/product/storefront/get-by-logic
export const getProductsByLogic = (logics: any) =>
  product.post<ProductItem[]>("/storefront/get-by-logic", logics);

// get product long description: /product/storefront/product-details/:slug
export const getProductLongDescription = (slug: string) =>
  product
    .get<{ longDescription: string }>(`/storefront/product-details/${slug}`)
    .then((res) => res.longDescription);

// get products by filter
interface ProductsProps {
  currentPage?: number;
  perPage?: number;
  filter?: string;
  query?: Object;
  config?: AxiosRequestConfig<any>;
  withoutPrefix?: boolean;
}
export const getProductsByFilter = ({
  currentPage = 1,
  perPage = 10,
  filter = "",
  query = "",
  config = {},
  withoutPrefix = false,
}: ProductsProps) =>
  product
    .v("1")
    .get<ProductListResponse<ProductItemV2>>(
      `/details-storefront?currentPage=${currentPage}&perPage=${perPage}${filter}&${product.buildQueryPrefix(
        query,
        withoutPrefix
      )}`,
      config
    );

export const getPriceMinMaxByFilter = ({
  currentPage = 1,
  perPage = 10,
  filter = "",
  query = "",
  config = {},
  withoutPrefix = false,
}: ProductsProps) =>
  product
    .v("1")
    .get<ProductListResponse<ProductItemV2>>(
      `/storefront-details/min-max?currentPage=${currentPage}&perPage=${perPage}${filter}&${product.buildQueryPrefix(
        query,
        withoutPrefix
      )}`,
      config
    );

export const getProductsBySearch = ({
  currentPage = 1,
  perPage = 10,
  filter = "",
  query = "",
  config = {},
}: ProductsProps) =>
  product
    .v("2")
    .get<ProductListResponse<ProductItemV2>>(
      `/storefront/search?currentPage=${currentPage}&perPage=${perPage}${filter}&${product.buildQueryPrefix(
        query
      )}`,
      config
    );

// getProductsByElasticSearch: {{apiUrl}}/api/v2/product/storefront/elastic-search
// export const getProductsByElasticSearch = ({
//   currentPage = 1,
//   perPage = 10,
//   search = "",
//   query = ""
// }: any) =>{
//   return product.v("1").post<any>(
//     `/storefront/search/elasticsearch`,
//     {
//       currentPage,
//       perPage,
//       search,
//       ...query
//     }
//   );
// }
  
export const getProductsByElasticSearch = ({
  currentPage = 1,
  perPage = 10,
  filter = "",
  query = ""
}: ProductsProps) =>
  product
    .v("1")
    .get<ProductListResponse<ProductItemV2>>(
      `/storefront/search/elasticsearch/v2?currentPage=${currentPage}&perPage=${perPage}${filter}&${product.buildQueryPrefix(
        query
      )}`,
      
    );

export const getSuggestedProductsByElasticSearch = ({
  searchText = "",
  limit = 10,
  config = {},
}: {
  searchText: string;
  limit?: number;
  config?: AxiosRequestConfig<any>;
}) =>
  product
    .v("1")
    .get<SuggestedSearchResponseV2>(
      `/storefront/search/suggestions/v2?search=${searchText}&perPage=${limit}`,
      config
    );

// post notify me
interface NotifyMeData {
  name?: string;
  phone?: string;
  email?: string;
  productSkuId: number;
}

export const postNotifyMe = (data: NotifyMeData) => notify.post("", data);

// relative products: product/storefront/related-products/:slug?limit=5
export const getRelativeProducts = (slug: string, limit: number) =>
  product.get<ProductItem[]>( 
    `/storefront/related-products/${slug}?limit=${limit}`
  );

// {{apiUrl}}/api/v1/product/storefront/fb-catalog
// Full-catalog feeds are ~12MB, well over Next.js' 2MB data-cache limit,
// so skip the data cache entirely for these (the XML feed routes still
// revalidate at the route level).
export const getFbCatalog = () =>
  product.get<FacebookFeedItem[]>("/storefront/fb-catalog/version-one");

// {{apiUrl}}/api/v1/product/storefront/fb-catalog
export const getFbCatalogV2 = () =>
  product.get<FacebookFeedItem[]>("/storefront/catalog/facebook");

// {{apiUrl}}/api/v1/product/storefront/product-wise-fb-catalog-with-thumbnail
export const getFbCatalogV3 = () =>
  product.get<FacebookFeedItemV2[]>("/storefront/catalog/product");

export const createProductReview = (data: any) =>
  review.post<ProductReviewResponse>("", data);

export const createProductReviewV2 = (data: any) =>
  review.post<ProductReviewResponse>("/product", data);

export const getProductReview = (slug: string, page: number, limit: number) =>
  review.get<ProductReviewResponse>(
    `?currentPage=${page}&perPage=${limit}&product_slug=${slug}&status=active`
  );

export const getProductQuestions = (id: string, page: number, limit: number) =>
  question.get<QuestionListResponse>(
    `/product/${id}?perPage=${limit}&currentPage=${page}`
  );

export const getRelatedProductsById = (id: number) => {
  return common.post<ProductListResponse<ProductItemV2>>("related-product/find", {
    product_ids: [id],
    isCategoryFilter: false,
  });
};

export const getSimilarProductBySlug = (slug: string) => {
  return product
    .v("2")
    .get<ProductListResponseV2>(`/storefront/similar-products/${slug}?limit=4`);
};

export const createQuestion = (data: any) => question.post<any>("", data);

interface FileUploadRes {
  success: boolean;
  data: [
    {
      fileName: string;
      fieldName: string;
    }
  ];
}

export const uploadReviewImage = (data: any) =>
  fileUpload.post<FileUploadRes>("?for=review", data);


export const blogsCommentsSubmit = (data: any) => question.post<any>("/blog", data);

export const getBlogsComments = (id: string, page: number, limit: number) =>
{
  return question.post<any>(`/blog/${id}`,{
    perPage: limit,
    currentPage: page
  })
}

// ── Admin: Product SKU ──

export interface SkuWarehouseStock {
  id: number;
  skuId: number;
  warehouseId: number;
  qty: number;
}

export interface SkuDiscountPrice {
  id: number;
  sku_id: number;
  discount_type: string;       // "percentage" | "fixed"
  discount_amount: number;
  discount_price: number;
  discount_start_date: string | null;
  discount_end_date: string | null;
  status: number;               // 1 = active
  priority: number;
  deleted_at: string | null;
  created_at: string;
}

export interface SkuAdminItem {
  id: number;
  productId: number;
  images: string;
  sku: string;
  customSku: string;
  barcode: null | string;
  sbarcode: null | string;
  stylecode: null | string;
  modelNo: null | string;
  vat: number;
  purchasePrice: null | number;
  price: number;
  discountedPrice: number;
  discountedType: null | string;
  discountedValue: number;
  discountedPriceStart: null | string;
  discountedPriceEnd: null | string;
  stockQuantity: number;
  bufferStock: number;
  preOrder: boolean;
  preOrderPercent: number;
  stockOut: boolean;
  comingSoon: boolean;
  status: string;
  attributes: ProductDetailsAttribute[];
  warehouseSkusStock: SkuWarehouseStock[];
  discountPrices: SkuDiscountPrice[];
}

interface ProductSkuListResponse {
  Success: boolean;
  Message: string;
  Data: SkuAdminItem[];
}

export const getProductSkus = (productId: number) =>
  common.get<ProductSkuListResponse>(`/product/sku/${productId}`);
